<template>
  <div class="dashboard pa-4">
    <!-- Admin User Management UI -->
    <v-card v-if="isAdmin && userProfile && userProfile.role !== 'department_admin' && userProfile.role !== 'department_member'" class="mb-6">
      <v-card-title class="d-flex align-center justify-space-between">
        <div>
          <v-icon color="primary" class="mr-2">mdi-account-cog</v-icon>
          User Management
        </div>
        <v-btn color="primary" @click="openInviteDialog">Invite User</v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="userTableHeaders"
          :items="users"
          item-key="uid"
          class="elevation-1"
        >
          <template #item.displayName="{ item }">
            <span>{{ item.displayName || item.email }}</span>
          </template>
          <template #item.email="{ item }">
            <span>{{ item.email }}</span>
          </template>
          <template #item.role="{ item }">
            <v-select
              :items="roleOptions"
              v-model="item.role"
              item-title="role"
              item-value="role"
              @change="role => updateUserRole(item, role)"
              variant="outlined"
              dense
              hide-details
              style="min-width: 200px; max-width: 260px"
            />
          </template>
          <template #item.actions="{ item }">
            <v-btn icon @click="sendPasswordReset(item.email)" :title="'Send password reset to ' + item.email">
              <v-icon color="primary">mdi-lock-reset</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="inviteDialog" max-width="500">
      <v-card>
        <v-card-title>Invite User</v-card-title>
        <v-card-text>
          <v-form ref="inviteForm" v-model="inviteFormValid">
            <v-text-field v-model="inviteEmail" label="Email" :rules="[v => !!v || 'Email required', v => /.+@.+\..+/.test(v) || 'Invalid email']" required></v-text-field>
            <v-select v-model="inviteRole" :items="roleOptions" label="Role" required></v-select>
            <v-text-field v-model="inviteName" label="Name (optional)"></v-text-field>
            <v-text-field v-model="inviteDepartment" label="Department (optional)"></v-text-field>
          </v-form>
          <v-alert v-if="inviteError" type="error" dense>{{ inviteError }}</v-alert>
          <v-alert v-if="inviteSuccess" type="success" dense>{{ inviteSuccess }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" :disabled="!inviteFormValid" @click="inviteUser">Send Invite</v-btn>
          <v-btn text @click="closeInviteDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="d-flex justify-space-between align-center mb-6">
      <span class="text-h5">{{ customerName }} Dashboard</span>
    </div>

    <v-row>
      <!-- Top Quality Aspects Card -->
      <v-col cols="12" md="6">
        <v-card class="mb-4">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-star</v-icon>
            Top Quality Aspects
          </v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item v-for="aspect in topQualityAspects" :key="aspect.id">
                <v-list-item-title class="font-weight-medium">{{ aspect.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  Potential: {{ aspect.potential }} (Want: {{ aspect.voteWhereWantToBe }}, Are: {{ aspect.voteWhereWeAre }})
                </v-list-item-subtitle>
                <v-list-item-subtitle class="mt-1">
                  Found in {{ aspect.workshopCount }} initiatives across {{ aspect.departmentCount }} departments and {{ aspect.projectCount }} projects
                </v-list-item-subtitle>
                <template #append>
                  <v-chip :color="getColorByPotential(aspect.potential)">
                    {{ aspect.potential }}
                  </v-chip>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Tasks Overview Card -->
      <v-col cols="12" md="6">
        <v-card class="mb-4">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-checkbox-marked</v-icon>
            Tasks Overview
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="6" class="text-center">
                <v-progress-circular
                  :model-value="taskStats.completionRate"
                  color="primary"
                  size="100"
                >
                  {{ taskStats.completionRate }}%
                </v-progress-circular>
                <div class="mt-2">Completion Rate</div>
              </v-col>
              <v-col cols="6">
                <div class="text-subtitle-1">Total Tasks: {{ taskStats.total }}</div>
                <div class="text-subtitle-1">Completed: {{ taskStats.completed }}</div>
                <div class="text-subtitle-1 text-grey">All tasks are completed</div>
                <div class="text-subtitle-1">In Progress: {{ taskStats.inProgress }}</div>
                <div class="text-subtitle-1">Not Started: {{ taskStats.notStarted }}</div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Department Overview -->
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-office-building</v-icon>
            Department Overview
          </v-card-title>
          <v-card-text>
            <v-expansion-panels multiple>
              <v-expansion-panel v-for="dept in departmentStats" :key="dept.id">
                <v-expansion-panel-title>
                  <v-icon color="primary" class="mr-2">mdi-office-building</v-icon>
                  {{ dept.name }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div>Projects: {{ dept.projectCount }}</div>
                  <div>Completed Initiatives: {{ dept.completedWorkshops }}</div>
                  <div>Tasks: {{ dept.completedTasks }} / {{ dept.totalTasks }}</div>
                  <v-progress-linear
                    :model-value="dept.taskCompletionRate"
                    color="success"
                    height="20"
                  >
                    <template #default="{ value }">
                      {{ Math.ceil(value) }}% Tasks Complete
                    </template>
                  </v-progress-linear>
                  <div class="mt-4 font-weight-medium">Top Quality Aspects:</div>
                  <v-list density="compact">
                    <v-list-item v-for="aspect in dept.topQualityAspects || []" :key="aspect.id">
                      <template #prepend>
                        <v-chip size="small" :color="getColorByPotential(aspect.potential)">
                          {{ aspect.potential }}
                        </v-chip>
                      </template>
                      <v-list-item-title class="text-subtitle-2">{{ aspect.name }}</v-list-item-title>
                      <v-list-item-subtitle>
                        Want: {{ aspect.voteWhereWantToBe }}, Are: {{ aspect.voteWhereWeAre }}
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                  <div v-if="departmentRiskScores[dept.id]" class="mt-2">
                    <v-chip size="small" :color="departmentRiskScores[dept.id].color">
                      Risk Score: {{ departmentRiskScores[dept.id].overall }}
                    </v-chip>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Project Timeline -->
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-chart-timeline-variant</v-icon>
            Project Timeline
          </v-card-title>
          <v-card-text>
            <v-expansion-panels multiple>
              <v-expansion-panel v-for="project in projectTimeline" :key="project.id">
                <v-expansion-panel-title>
                  <v-icon color="primary" class="mr-2">mdi-chart-timeline-variant</v-icon>
                  {{ project.name }}
                  <v-chip :color="getProjectStatusColor(project.status)" size="small" class="ml-2">{{ project.status }}</v-chip>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div>Department: {{ project.department }}</div>
                  <div>Start Date: {{ formatDate(project.startDate) }}</div>
                  <div>Completed Workshops: {{ project.completedWorkshops }} / {{ project.totalWorkshops }}</div>
                  <div class="font-weight-medium mt-2">Top Quality Aspects:</div>
                  <v-list density="compact">
                    <v-list-item v-for="aspect in project.topQualityAspects || []" :key="aspect.id">
                      <template #prepend>
                        <v-chip size="x-small" :color="getColorByPotential(aspect.potential)">
                          {{ aspect.potential }}
                        </v-chip>
                      </template>
                      <v-list-item-title class="text-subtitle-2">{{ aspect.name }}</v-list-item-title>
                      <v-list-item-subtitle>
                        Want: {{ aspect.voteWhereWantToBe }}, Are: {{ aspect.voteWhereWeAre }}
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                  <div v-if="projectRiskScores[project.id]" class="mt-2">
                    <v-chip size="small" :color="projectRiskScores[project.id].color">
                      Risk Score: {{ projectRiskScores[project.id].overall }}
                    </v-chip>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- After Project Timeline -->
    <v-row>
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-folder-open</v-icon>
            Projects & Initiatives (Grouped by Department)
          </v-card-title>
          <v-card-text>
            <v-expansion-panels multiple>
              <v-expansion-panel v-for="dept in groupedDepartments" :key="dept.id">
                <v-expansion-panel-title>
                  <span class="font-weight-bold">{{ dept.name }}</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-expansion-panels multiple>
                    <v-expansion-panel v-for="project in dept.projects" :key="project.id">
                      <v-expansion-panel-title>
                        <span>{{ project.name }}</span>
                        <v-chip :color="getProjectStatusColor(project.status)" size="small" class="ml-2">{{ project.status }}</v-chip>
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <div class="text-caption mb-2">Department: {{ dept.name }}</div>
                        <div class="font-weight-medium mb-1">Initiatives:</div>
                        <v-list dense>
                          <v-list-item
                            v-for="initiative in project.initiatives"
                            :key="initiative.id"
                            @click="goToInitiative(dept.id, project.id, initiative.id)"
                            style="cursor:pointer"
                          >
                            <template #prepend>
                              <v-icon color="primary">mdi-lightbulb-on-outline</v-icon>
                            </template>
                            <v-list-item-title>{{ initiative.name }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item v-if="project.initiatives.length === 0">
                            <v-list-item-title>No initiatives found.</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-btn color="primary" @click="openNewInitiativeDialog" v-if="Array.isArray(projectTimeline) && projectTimeline.length">
      + Add Initiative
    </v-btn>
    <v-dialog v-model="newInitiativeDialog" max-width="400">
      <v-card>
        <v-card-title>Add Initiative</v-card-title>
        <v-card-text>
          <v-text-field v-model="newInitiativeName" label="Initiative Name"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="createInitiative">Create</v-btn>
          <v-btn text @click="newInitiativeDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { loadCustomerDashboard } from '@/workshop/dashboardFunctions';
import { calculateCompositeRiskScore } from '../utils/riskScore';
import { getDatabase, ref as dbRef, set, get, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import router from '@/router';
import segmentData from '@/data/segment.json';

// Utility: Flatten segment.json to get aspectId-to-name mapping
function buildAspectIdToNameMap(segmentData) {
  const map = {};
  segmentData.forEach(main => {
    if (main.children) {
      main.children.forEach(child => {
        map[child.id] = child.text?.en?.name || child.id;
      });
    }
  });
  return map;
}
const aspectIdToName = buildAspectIdToNameMap(segmentData);

console.log('[DEBUG] aspectIdToName:', aspectIdToName);

export default {
  name: 'CustomerDashboard',
  props: {
    customerId: {
      type: String,
      required: true
    }
  },
  setup() {
    console.log('[DEBUG] setup() called in CustomerDashboard.vue');
    const store = useStore();
    console.log('[DEBUG] userProfile at setup:', store.getters.userProfile);
    // Admin User Management
    const users = ref([]);
    const departmentsMap = ref({});
    const userTableHeaders = [
      { text: 'Name', value: 'displayName' },
      { text: 'Email', value: 'email' },
      { text: 'Role', value: 'role' },
      { text: 'Department', value: 'departmentName' },
      { text: 'Project', value: 'projectId' },
      { text: 'Actions', value: 'actions', sortable: false }
    ];
    const roleOptions = [
      'super_admin',
      'company_admin',
      'department_admin',
      'project_admin',
      'company_member',
      'department_member',
      'project_member',
      'guest'
    ];
    const userProfile = computed(() => store.getters.userProfile);
    const isAdmin = computed(() => {
      return userProfile.value && [
        'super_admin',
        'company_admin',
        'department_admin',
        'project_admin'
      ].includes(userProfile.value.role);
    });
    // Fetch departments for the current company (department admin/member: only their department)
    const fetchDepartments = async () => {
      if (!userProfile.value || !userProfile.value.companyId) return;
      const db = getDatabase();
      if (userProfile.value.role === 'department_admin' || userProfile.value.role === 'department_member') {
        const deptRef = dbRef(db, `customers/${userProfile.value.companyId}/departments/${userProfile.value.departmentId}`);
        const snap = await get(deptRef);
        if (snap.exists()) {
          departmentsMap.value = { [userProfile.value.departmentId]: snap.val().name };
          console.log('[DEBUG] Loaded department:', snap.val());
          console.log('[DEBUG] user.departmentId:', userProfile.value.departmentId);
          console.log('[DEBUG] Project keys:', Object.keys(snap.val().projects || {}));
          Object.entries(snap.val().projects || {}).forEach(([projectId, project]) => {
            console.log(`[DEBUG] Project ${projectId}:`, project);
            console.log(`[DEBUG] Workshop keys for project ${projectId}:`, Object.keys(project.workshops || {}));
          });
        } else {
          departmentsMap.value = {};
        }
      } else {
        const deptsRef = dbRef(db, `customers/${userProfile.value.companyId}/departments`);
        const snap = await get(deptsRef);
        if (snap.exists()) {
          departmentsMap.value = Object.fromEntries(
            Object.entries(snap.val()).map(([id, dept]) => [id, dept.name])
          );
        } else {
          departmentsMap.value = {};
        }
      }
    };
    // Fetch users for the current company
    const fetchUsers = async () => {
      if (!userProfile.value || !userProfile.value.companyId) return;
      const db = getDatabase();
      const usersRef = dbRef(db, 'users');
      const snap = await get(usersRef);
      if (snap.exists()) {
        // Filter users by companyId
        const allUsers = Object.entries(snap.val()).map(([uid, user]) => ({ ...user, uid }));
        users.value = allUsers.filter(u => u.companyId === userProfile.value.companyId).map(u => ({
          ...u,
          departmentName: departmentsMap.value[u.departmentId] || u.departmentId
        }));
      } else {
        users.value = [];
      }
    };
    // Update user role in Realtime Database
    const updateUserRole = async (user, newRole) => {
      const db = getDatabase();
      const userRef = dbRef(db, `users/${user.uid}/role`);
      await set(userRef, newRole);
      user.role = newRole;
    };

    // Existing dashboard logic
    const customerName = ref('');
    const topQualityAspects = ref([]);
    const taskStats = ref({
      total: 0,
      completed: 0,
      inProgress: 0,
      notStarted: 0,
      completionRate: 0
    });
    const departmentStats = ref([]);
    const projectTimeline = ref([]);

    // Add watcher for userProfile
    watch(
      () => store.getters.userProfile,
      async (user) => {
        if (!user) return;
        if (user.role === 'department_admin' || user.role === 'department_member') {
          // Only fetch their department node
          const db = getDatabase();
          const deptRef = dbRef(db, `customers/${user.companyId}/departments/${user.departmentId}`);
          const snap = await get(deptRef);
          const dept = snap.exists() ? snap.val() : null;
          if (dept) {
            console.log('[DEBUG] Loaded department:', dept);
            console.log('[DEBUG] user.departmentId:', user.departmentId);
            console.log('[DEBUG] Project keys:', Object.keys(dept.projects || {}));
            Object.entries(dept.projects || {}).forEach(([projectId, project]) => {
              console.log(`[DEBUG] Project ${projectId}:`, project);
              console.log(`[DEBUG] Workshop keys for project ${projectId}:`, Object.keys(project.workshops || {}));
            });
            customerName.value = dept.name || 'Department';
            // Aggregate all projects and workshops with mapping
            let aspectMap = {};
            let totalTasks = 0, completed = 0, inProgress = 0, notStarted = 0;
            let timeline = [];
            let deptCompletedWorkshops = 0;
            let deptProjects = [];
            Object.entries(dept.projects || {}).forEach(([projectId, project]) => {
              let projectCompletedWorkshops = 0;
              let projectTotalWorkshops = 0;
              let projectAspects = {};
              let projectTasks = [];
              Object.values(project.workshops || {}).forEach(workshop => {
                projectTotalWorkshops++;
                if (workshop && Object.keys(workshop).length > 0) projectCompletedWorkshops++;
                // Aggregate aspects from results
                if (workshop.results) {
                  Object.values(workshop.results).forEach(result => {
                    if (result && typeof result.voteWhereWantToBe !== 'undefined' && typeof result.voteWhereWeAre !== 'undefined') {
                      const potential = Number(result.voteWhereWantToBe) - Number(result.voteWhereWeAre);
                      if (!aspectMap[result.id]) {
                        aspectMap[result.id] = {
                          id: result.id,
                          name: aspectIdToName[result.id] || result.id,
                          voteWhereWantToBe: Number(result.voteWhereWantToBe),
                          voteWhereWeAre: Number(result.voteWhereWeAre),
                          potential,
                          workshopCount: 0,
                          departmentSet: new Set(),
                          projectSet: new Set()
                        };
                      }
                      aspectMap[result.id].workshopCount++;
                      aspectMap[result.id].departmentSet.add(user.departmentId);
                      aspectMap[result.id].projectSet.add(projectId);
                      // Project-level (fix: do not leak Set objects)
                      if (!projectAspects[result.id]) {
                        projectAspects[result.id] = {
                          id: result.id,
                          name: aspectIdToName[result.id] || result.id,
                          voteWhereWantToBe: Number(result.voteWhereWantToBe),
                          voteWhereWeAre: Number(result.voteWhereWeAre),
                          potential,
                          workshopCount: 0,
                          departmentCount: 1, // Always 1 for dept admin
                          projectCount: 1     // Always 1 for dept admin
                        };
                      }
                      projectAspects[result.id].workshopCount++;
                    }
                  });
                }
                // Aggregate tasks if present
                if (workshop.tasks) {
                  const tasks = Object.values(workshop.tasks);
                  projectTasks = projectTasks.concat(tasks);
                  totalTasks += tasks.length;
                  completed += tasks.filter(t => (t.status || '').toLowerCase() === 'completed').length;
                  inProgress += tasks.filter(t => (t.status || '').toLowerCase() === 'in progress').length;
                  notStarted += tasks.filter(t => !t.status || t.status.toLowerCase() === 'not started').length;
                }
              });
              deptCompletedWorkshops += projectCompletedWorkshops;
              deptProjects.push({
                id: projectId,
                name: project.name,
                department: dept.name,
                startDate: project.startDate || new Date().toISOString(),
                status: 'In Progress',
                totalWorkshops: projectTotalWorkshops,
                completedWorkshops: projectCompletedWorkshops,
                topQualityAspects: Object.values(projectAspects), // Already mapped, no Sets
                workshops: project.workshops // For initiative display
              });
              timeline.push({
                id: projectId,
                name: project.name,
                department: dept.name,
                startDate: project.startDate || new Date().toISOString(),
                status: 'In Progress',
                totalWorkshops: projectTotalWorkshops,
                completedWorkshops: projectCompletedWorkshops,
                topQualityAspects: Object.values(projectAspects), // Already mapped, no Sets
                workshops: project.workshops
              });
            });
            // Map aspects for UI
            topQualityAspects.value = Object.values(aspectMap).map(a => ({
              ...a,
              departmentCount: a.departmentSet.size,
              projectCount: a.projectSet.size
            }));
            taskStats.value = {
              total: totalTasks,
              completed,
              inProgress,
              notStarted,
              completionRate: totalTasks ? Math.round((completed / totalTasks) * 100) : 0
            };
            departmentStats.value = [{
              id: user.departmentId,
              name: dept.name,
              projectCount: deptProjects.length,
              completedWorkshops: deptCompletedWorkshops,
              totalTasks,
              completedTasks: completed,
              taskCompletionRate: totalTasks ? Math.round((completed / totalTasks) * 100) : 0,
              topQualityAspects: topQualityAspects.value,
              projects: deptProjects
            }];
            projectTimeline.value = timeline;
            // Expose deptProjects for template
            window.deptProjects = deptProjects;
            console.log('[DEBUG] projectTimeline for initiatives:', projectTimeline.value);
          }
        } else if (isAdmin.value) {
          await fetchDepartments();
          await fetchUsers();
        }
      },
      { immediate: true }
    );

    onMounted(async () => {
      const user = store.getters.userProfile;
      if (!user) return;
      if (user.role === 'company_admin' || user.role === 'super_admin') {
        let companyId = user.companyId;
        // If super_admin and no companyId, pick the first company
        if (user.role === 'super_admin' && !companyId) {
          const db = getDatabase();
          const customersRef = dbRef(db, 'customers');
          const customersSnap = await get(customersRef);
          const customers = customersSnap.exists() ? customersSnap.val() : {};
          companyId = Object.keys(customers)[0]; // Pick the first company
        }
        if (companyId) {
          let dashboardData = await loadCustomerDashboard(companyId);
          customerName.value = dashboardData.customerName;
          topQualityAspects.value = dashboardData.topQualityAspects;
          taskStats.value = dashboardData.taskStats;
          departmentStats.value = dashboardData.departmentStats;
          projectTimeline.value = dashboardData.projectTimeline;
        }
      }
      // For department_admin and department_member, do nothing here; watcher handles data fetching.
    });

    const getColorByPotential = (potential) => {
      if (potential >= 3) return 'error';
      if (potential >= 2) return 'warning';
      return 'success';
    };

    const getProjectStatusColor = (status) => {
      const colors = {
        'Not Started': 'grey',
        'In Progress': 'primary',
        'Completed': 'success'
      };
      return colors[status] || 'grey';
    };

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString();
    };

    // Helper to compute risk score from aspects
    function computeRiskScore(aspects) {
      if (!aspects || !aspects.length) return 0;
      const scores = aspects.map(a => calculateCompositeRiskScore(a.voteWhereWeAre, a.voteWhereWantToBe));
      return scores.reduce((sum, s) => sum + s, 0) / scores.length;
    }
    function getRiskColor(score) {
      if (score > 5) return 'error';
      if (score >= 3) return 'warning';
      return 'success';
    }

    // Department risk scores
    const departmentRiskScores = computed(() => {
      return departmentStats.value.reduce((acc, dept) => {
        const overall = computeRiskScore(dept.topQualityAspects);
        acc[dept.id] = {
          overall: overall.toFixed(2),
          color: getRiskColor(overall)
        };
        return acc;
      }, {});
    });
    // Project risk scores
    const projectRiskScores = computed(() => {
      const scores = {};
      projectTimeline.value.forEach(project => {
        const overall = computeRiskScore(project.topQualityAspects);
        scores[project.id] = {
          overall: overall.toFixed(2),
          color: getRiskColor(overall)
        };
      });
      return scores;
    });

    const goToInitiative = (departmentId, projectId, initiativeId) => {
      const user = store.getters.userProfile;
      router.push({
        name: 'Workshop',
        params: {
          customerId: user.companyId,
          departmentId: departmentId,
          projectId,
          id: initiativeId
        }
      });
    };

    const newInitiativeDialog = ref(false);
    const newInitiativeName = ref('');

    const openNewInitiativeDialog = () => {
      newInitiativeDialog.value = true;
    };

    const createInitiative = async () => {
      const user = store.getters.userProfile;
      const db = getDatabase();
      // Use the first project in the department
      const dept = projectTimeline.value.length ? departmentStats.value[0] : null;
      if (!dept || !dept.projectCount) {
        alert('No project found to add initiative.');
        return;
      }
      // Find the first projectId
      const firstProjectId = Object.keys(dept.projects)[0];
      const initiativesRef = dbRef(db, `customers/${user.companyId}/departments/${user.departmentId}/projects/${firstProjectId}/workshops`);
      const newInitiativeRef = push(initiativesRef);
      await set(newInitiativeRef, {
        id: newInitiativeRef.key,
        name: newInitiativeName.value
      });
      newInitiativeDialog.value = false;
      newInitiativeName.value = '';
      // Optionally reload data here
      location.reload();
    };

    const inviteDialog = ref(false);
    const inviteEmail = ref('');
    const inviteRole = ref('');
    const inviteName = ref('');
    const inviteDepartment = ref('');
    const inviteFormValid = ref(false);
    const inviteError = ref('');
    const inviteSuccess = ref('');
    const inviteForm = ref(null);

    function openInviteDialog() {
      inviteDialog.value = true;
      inviteEmail.value = '';
      inviteRole.value = '';
      inviteName.value = '';
      inviteDepartment.value = '';
      inviteError.value = '';
      inviteSuccess.value = '';
    }

    function closeInviteDialog() {
      inviteDialog.value = false;
    }

    async function inviteUser() {
      inviteError.value = '';
      inviteSuccess.value = '';
      if (!inviteEmail.value || !inviteRole.value) {
        inviteError.value = 'Email and role are required.';
        return;
      }
      try {
        // Create user in Firebase Auth (if not exists)
        const { sendPasswordResetEmail } = await import('firebase/auth');
        const auth = getAuth();
        await sendPasswordResetEmail(auth, inviteEmail.value);
        inviteSuccess.value = 'Invitation sent! The user will receive a password setup email.';
        await fetchUsers();
      } catch (err) {
        inviteError.value = err.message || 'Failed to invite user.';
      }
    }

    async function sendPasswordReset(email) {
      try {
        const { sendPasswordResetEmail } = await import('firebase/auth');
        const auth = getAuth();
        await sendPasswordResetEmail(auth, email);
        alert('Password reset email sent to ' + email);
      } catch (err) {
        alert('Failed to send password reset: ' + (err.message || err));
      }
    }

    // Add this computed property after your other refs and computeds
    const groupedDepartments = computed(() => {
      return (departmentStats.value || []).map(dept => ({
        id: dept.id,
        name: dept.name,
        projects: (dept.projects || []).map(project => ({
          ...project,
          initiatives: Object.entries(project.workshops || {}).map(([id, w]) => ({ id, ...w }))
        }))
      }));
    });

    return {
      // Admin UI
      users,
      userTableHeaders,
      roleOptions,
      isAdmin,
      updateUserRole,
      // Existing dashboard
      customerName,
      topQualityAspects,
      taskStats,
      departmentStats,
      projectTimeline,
      getColorByPotential,
      getProjectStatusColor,
      formatDate,
      departmentRiskScores,
      projectRiskScores,
      goToInitiative,
      newInitiativeDialog,
      openNewInitiativeDialog,
      createInitiative,
      inviteDialog,
      inviteEmail,
      inviteRole,
      inviteName,
      inviteDepartment,
      inviteFormValid,
      inviteError,
      inviteSuccess,
      inviteForm,
      openInviteDialog,
      closeInviteDialog,
      inviteUser,
      sendPasswordReset,
      groupedDepartments
    };
  }
};
</script>

<style scoped>
.dashboard {
  max-width: 1400px;
  margin: 0 auto;
}

.text-h5 {
  font-size: 1.5rem;
  font-weight: 500;
}

:deep(.v-timeline-item__body) {
  padding: 12px !important;
}

:deep(.v-card-text) {
  padding: 16px;
}

:deep(.v-list-item) {
  padding: 12px;
}
</style>
